///
/// EarthBrowser
///
/// Hook to automatically scan local data directory on launch
///
/// Created by Matt Giger on December 23, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { setLocalDataScanResults, clearLocalDataScanResults } from '@/redux/dialogSlice'
import { clearLocalDataDirectory, type LocalDataFile } from '@/redux/earthBrowserSlice'
import { getFileSystemAccessManager } from '@/earthbrowser/filesystem/FileSystemAccessManager'
import { LocalDataScanner } from '@/earthbrowser/filesystem/LocalDataScanner'
import { logger } from '@/utils/LogDispatcher'

/**
 * Hook that automatically scans the configured local data directory on mount
 * and when the directory ID changes
 */
export function useLocalDataDirectoryScanner() {
	const dispatch = useAppDispatch()
	const localDataDirectoryId = useAppSelector(state => state.earthbrowser.localDataDirectoryId)

	useEffect(() => {
		// Skip if no directory is configured
		if (!localDataDirectoryId) {
			return
		}

		let cancelled = false

		const scanDirectory = async () => {
			try {
				const fsManager = getFileSystemAccessManager()
				await fsManager.initialize()

				const dirHandle = await fsManager.getDirectoryHandle(localDataDirectoryId)

				// If handle is no longer accessible, clear the directory setting
				if (!dirHandle) {
					logger.warn('[Local Data Scanner] Directory no longer accessible, clearing setting')
					dispatch(clearLocalDataDirectory())
					dispatch(clearLocalDataScanResults())
					return
				}

				// Check if we were cancelled before starting the scan
				if (cancelled) return

				logger.info('[Local Data Scanner] Starting scan of local data directory')
				const result = await LocalDataScanner.scanDirectory(dirHandle)

				// Check if we were cancelled during the scan
				if (cancelled) return

				// Convert to serializable format (remove handle references)
				const files: LocalDataFile[] = result.files.map(f => ({
					name: f.name,
					path: f.path,
					type: f.type,
					size: f.size,
					lastModified: f.lastModified
				}))

				dispatch(setLocalDataScanResults({
					files,
					scannedAt: result.scannedAt,
					errors: result.errors
				}))

				logger.info(`[Local Data Scanner] Scan complete: found ${files.length} files`)
			} catch (error) {
				if (!cancelled) {
					logger.error('[Local Data Scanner] Failed to scan directory', error)
					dispatch(clearLocalDataScanResults())
				}
			}
		}

		void scanDirectory()

		// Cleanup function to cancel scan if component unmounts or directory changes
		return () => {
			cancelled = true
		}
	}, [localDataDirectoryId, dispatch])
}

